import { formatPhp, formatDate } from "@/lib/calc";

// Underlined inline input for officer-filled values.
function Ln({ value, onChange, placeholder, width = "min-w-[140px]", type = "text", testid, mono = false }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className={`inline-block ${width} bg-transparent border-b border-slate-400 outline-none px-1 text-[13px] text-slate-900 ${
        mono ? "tabular-nums" : ""
      } placeholder:text-slate-300 focus:bg-yellow-50`}
    />
  );
}

// Read-only computed cell (bold).
function Val({ children, testid }) {
  return (
    <span className="font-semibold tabular-nums text-slate-900" data-testid={testid}>
      {children}
    </span>
  );
}

function SigLine({ caption, value }) {
  return (
    <div className="mt-10">
      <div className="border-t border-slate-900 pt-1 min-h-[1.5rem]">
        <span className="font-serif-display text-base text-slate-900">{value || "\u00A0"}</span>
      </div>
      <p className="text-[10px] text-slate-600 leading-tight mt-1">{caption}</p>
    </div>
  );
}

export default function DisclosureStatement({
  form,
  set,
  charges,
  contractualRatePct,
  interestAmount,
  dst,
  totalDeductions,
  netProceeds,
  eirPct,
  monthlyAmort,
  dateGranted,
  maturityDate,
  firstPaymentDate,
  loanAmountStr,
  onLoanAmount,
  termOptions = [],
  showLogo = true,
}) {
  const LOAN_PRODUCT = "JewelFlex Loan";
  const fromDate = formatDate(firstPaymentDate);
  const toDate = formatDate(maturityDate);

  return (
    <div className="disclosure-doc text-[13px] leading-relaxed text-slate-900" data-testid="disclosure-statement">
      {/* Header */}
      <div className="text-center border-b-2 border-slate-900 pb-4 mb-5 break-inside-avoid">
        {showLogo && (
          <img src="/logo.png" alt="Logo" className="print-logo h-20 w-auto object-contain mx-auto mb-2" />
        )}
        <h1 className="text-lg font-bold tracking-wide">CEBUANA LHUILLIER RURAL BANK</h1>
        <h2 className="text-sm font-bold uppercase mt-1">Disclosure Statement on Loan/Credit Transaction</h2>
        <p className="text-[11px] italic text-slate-600">(As Required under R.A. 3765, Truth in Lending Act)</p>
      </div>

      {/* Borrower block */}
      <div className="space-y-2 mb-5">
        <div>
          <span className="font-semibold">Name of the Borrower: </span>
          <Ln value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" width="min-w-[320px]" testid="disc-name" />
        </div>
        <div>
          <span className="font-semibold">Address: </span>
          <Ln value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Complete address" width="min-w-[360px]" testid="disc-address" />
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          <div>
            <span className="font-semibold">Loan Product: </span>
            <span data-testid="disc-loan-product">{LOAN_PRODUCT}</span>
          </div>
          <div>
            <span className="font-semibold">Loan Account Number: </span>
            <Ln value={form.loanAccountNumber} onChange={(e) => set("loanAccountNumber", e.target.value)} placeholder="Account no." testid="disc-loan-account" />
          </div>
        </div>
        <div>
          <span className="font-semibold">Date Granted: </span>
          <span data-testid="disc-date-granted">{formatDate(dateGranted)}</span>
        </div>
      </div>

      {/* Loan granted */}
      <div className="mb-4 font-semibold flex items-center gap-1 flex-wrap">
        LOAN GRANTED (Amount to be financed) PHP
        <input
          type="text"
          inputMode="decimal"
          value={loanAmountStr}
          onChange={onLoanAmount}
          placeholder="0.00"
          data-testid="disc-loan-granted-input"
          className="inline-block min-w-[160px] bg-transparent border-b border-slate-400 outline-none px-1 text-[14px] font-semibold tabular-nums text-slate-900 placeholder:text-slate-300 focus:bg-yellow-50"
        />
      </div>

      {/* Finance charges table */}
      <table className="w-full border border-slate-900 border-collapse text-[12px] mb-5 break-inside-avoid">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-900 p-2 text-left">FINANCE CHARGES</th>
            <th className="border border-slate-900 p-2 text-center w-[26%]">Not Deducted From Proceeds</th>
            <th className="border border-slate-900 p-2 text-center w-[26%]">Deducted From Proceeds</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-slate-900 p-2">
              Interest {contractualRatePct}% add-on per month
              <div className="text-[11px] text-slate-600 mt-1">
                From {fromDate} To {toDate}
              </div>
            </td>
            <td className="border border-slate-900 p-2 text-right"><Val testid="disc-interest-amt">{formatPhp(interestAmount)}</Val></td>
            <td className="border border-slate-900 p-2 text-right">&nbsp;</td>
          </tr>
          <tr>
            <td className="border border-slate-900 p-2">Other charges incidental to the extension of credit:</td>
            <td className="border border-slate-900 p-2">&nbsp;</td>
            <td className="border border-slate-900 p-2">&nbsp;</td>
          </tr>
          <tr>
            <td className="border border-slate-900 p-2 pl-6">c.1. Documentary Stamp</td>
            <td className="border border-slate-900 p-2">&nbsp;</td>
            <td className="border border-slate-900 p-2 text-right"><Val testid="disc-dst">{formatPhp(dst)}</Val></td>
          </tr>
          <tr className="font-semibold">
            <td className="border border-slate-900 p-2">TOTAL DEDUCTIONS FROM PROCEEDS OF LOAN</td>
            <td className="border border-slate-900 p-2">&nbsp;</td>
            <td className="border border-slate-900 p-2 text-right"><Val testid="disc-total-deductions">{formatPhp(totalDeductions)}</Val></td>
          </tr>
          <tr className="font-semibold">
            <td className="border border-slate-900 p-2">NET PROCEEDS OF LOAN</td>
            <td className="border border-slate-900 p-2">&nbsp;</td>
            <td className="border border-slate-900 p-2 text-right"><Val testid="disc-net-proceeds">{formatPhp(netProceeds)}</Val></td>
          </tr>
        </tbody>
      </table>

      {/* EIR */}
      <div className="mb-5 break-inside-avoid">
        <p className="font-semibold">EFFECTIVE INTEREST RATE</p>
        <p className="text-[12px] text-slate-700 mt-1">
          The effective interest rate is higher than the contractual interest rate of {contractualRatePct}% because of the deductions above.
        </p>
        <p className="mt-1 text-base"><Val testid="disc-eir">{eirPct}%</Val></p>
      </div>

      {/* Schedule of payment */}
      <div className="mb-5 break-inside-avoid">
        <p className="font-semibold">SCHEDULE OF PAYMENT</p>
        <p className="mt-1"><span className="font-semibold">Payment Frequency:</span> Monthly</p>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <span>Total Installment Payments Payable in</span>
          <select
            value={form.termMonths}
            onChange={(e) => set("termMonths", Number(e.target.value))}
            data-testid="disc-term-select"
            className="bg-transparent border-b border-slate-400 outline-none px-1 font-semibold cursor-pointer focus:bg-yellow-50"
          >
            {(termOptions.length ? termOptions : [6, 7, 8, 9, 10, 11, 12]).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <span>months at PHP</span>
          <Val testid="disc-installment-amt">{formatPhp(monthlyAmort).replace("₱", "")}</Val>
          <span>each installment, starting on</span>
          <Val testid="disc-first-installment-date">{fromDate}</Val>
          <span>and thereafter on every corresponding date of the succeeding agreed installment periods.</span>
        </div>
      </div>

      {/* Collateral */}
      <div className="mb-5">
        <p className="font-semibold">COLLATERAL</p>
        <label className="flex items-center gap-2 mt-1 cursor-pointer">
          <input type="checkbox" checked={!!form.collateralJewelry} onChange={() => set("collateralJewelry", !form.collateralJewelry)} data-testid="disc-collateral-jewelry" />
          <span>This loan is wholly/partly secured by: Jewelry</span>
        </label>
      </div>

      {/* Conditional charges */}
      <div className="mb-5 break-inside-avoid">
        <p className="font-semibold">ADDITIONAL/CONDITIONAL CHARGES IN CASE CERTAIN STIPULATIONS ARE NOT MET BY THE BORROWER</p>
        <table className="w-full border border-slate-900 border-collapse text-[12px] mt-2">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-900 p-2 text-left w-[32%]">Nature</th>
              <th className="border border-slate-900 p-2 text-left">Rate / Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-900 p-2">Missed Payment Penalty</td>
              <td className="border border-slate-900 p-2" data-testid="disc-missed-penalty">{charges.missed_payment_penalty}</td>
            </tr>
            <tr>
              <td className="border border-slate-900 p-2">Prepayment Charges</td>
              <td className="border border-slate-900 p-2" data-testid="disc-prepayment-charges">
                {charges.prepayment_charges || "2% full interest amount scheduled for the month of prepayment."}
              </td>
            </tr>
            <tr>
              <td className="border border-slate-900 p-2">Past Due Interest</td>
              <td className="border border-slate-900 p-2" data-testid="disc-pastdue-interest">{charges.past_due_interest}</td>
            </tr>
            <tr>
              <td className="border border-slate-900 p-2">Past Due Penalty</td>
              <td className="border border-slate-900 p-2" data-testid="disc-pastdue-penalty">{charges.past_due_penalty}</td>
            </tr>
            <tr>
              <td className="border border-slate-900 p-2">Attorney&apos;s Fees</td>
              <td className="border border-slate-900 p-2" data-testid="disc-attorney-fees">{charges.attorneys_fees}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Signatures */}
      <div className="break-inside-avoid mt-6">
        <p className="font-semibold">CERTIFIED CORRECT:</p>
        <div className="w-2/3">
          <SigLine caption="(Bank's Authorized Representative)" value="" />
        </div>

        <p className="text-[11px] mt-8 leading-snug">
          I ACKNOWLEDGE RECEIPT OF A COPY OF THIS STATEMENT PRIOR TO THE CONSUMMATION OF THE CREDIT TRANSACTION AND THAT I
          UNDERSTAND AND FULLY AGREE TO THE TERMS AND CONDITIONS THEREOF.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12">
          <SigLine caption="(Signature over printed name / Date) — BORROWER" value={form.name} />
          <SigLine caption="(Signature over printed name / Date) — BRANCH PAWNSHOP REPRESENTATIVE (WITNESS)" value="" />
        </div>
      </div>
    </div>
  );
}
